
import { Component, ChangeDetectionStrategy, input, output, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { PosDevice, Store, PrinterSettings } from '../../models';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-pos-device-form-modal',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: './pos-device-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosDeviceFormModalComponent {
  private notificationService = inject(NotificationService);
  
  device = input<PosDevice | null>();
  stores = input.required<Store[]>();
  isOpen = input.required<boolean>();
  
  closeModal = output<void>();
  saveDevice = output<Omit<PosDevice, 'id' | 'createdAt'> | PosDevice>();

  deviceForm: FormGroup;
  
  printerModels = [
    'No Printer',
    'Star TSP654IIBI Bluetooth',
    'Star TSP143IIILAN Ethernet',
    'Star mPOP Bluetooth',
    'Star mC-Print3',
    'Epson TM-T20II Ethernet',
    'Epson TM-T88V Ethernet',
    'Epson TM-m30',
    'Sunmi',
    'Posiflex 6900',
    'Xprinter XP-Q800',
    'GP-58130IIC',
    'GP-U80300I',
    'GP-L80250I',
    'Other',
  ];

  connectionTypes = ['NO_PRINTER', 'ETHERNET', 'BLUETOOTH', 'USB'];
  paperWidths = ['80mm', '72mm', '58mm'];
  printModes = ['GRAPHIC', 'TEXT'];
  printResolutions = ['203dpi', '180dpi'];

  constructor() {
    const fb: FormBuilder = inject(FormBuilder);
    this.deviceForm = fb.group({
      id: [null],
      name: ['', Validators.required],
      storeId: [null, Validators.required],
      status: ['active', Validators.required],
      printerSettings: fb.group({
        model: ['No Printer'],
        connectionType: ['NO_PRINTER', Validators.required],
        ipAddress: [null],
        paperWidth: ['80mm', Validators.required],
        autoPrint: [false],
        advancedSettingsEnabled: [false],
        advancedSettings: fb.group({
          printMode: ['GRAPHIC'],
          printWidth: ['72mm'],
          printResolution: ['203dpi'],
          initialCommand: [''],
          cutterCommand: ['1D,56,42,00'],
          drawerCommand: ['1B,70,00,19,FA'],
        })
      })
    });

    this.deviceForm.get('printerSettings.connectionType')?.valueChanges.subscribe(type => {
      const ipAddressControl = this.deviceForm.get('printerSettings.ipAddress');
      if (type === 'ETHERNET') {
        ipAddressControl?.setValidators([Validators.required, Validators.pattern(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/)]);
      } else {
        ipAddressControl?.clearValidators();
      }
      ipAddressControl?.updateValueAndValidity();
    });

    effect(() => {
      const deviceData = this.device();
      if (this.isOpen()) {
        if (deviceData) {
          this.deviceForm.patchValue({
            id: deviceData.id,
            name: deviceData.name,
            storeId: deviceData.storeId,
            status: deviceData.status,
            printerSettings: {
              ...this.getDefaultPrinterSettings(),
              ...(deviceData.printerSettings || {}),
              advancedSettings: {
                ...this.getDefaultAdvancedSettings(),
                ...(deviceData.printerSettings?.advancedSettings || {})
              }
            }
          });
        } else {
          this.deviceForm.reset({
            id: null,
            name: '',
            storeId: this.stores().length > 0 ? this.stores()[0].id : null,
            status: 'active',
            printerSettings: this.getDefaultPrinterSettingsWithAdvanced()
          });
        }
      }
    });
  }

  private getDefaultPrinterSettings() {
    return {
      model: 'No Printer',
      connectionType: 'NO_PRINTER',
      ipAddress: null,
      paperWidth: '80mm',
      autoPrint: false,
      advancedSettingsEnabled: false
    };
  }

  private getDefaultAdvancedSettings() {
    return {
      printMode: 'GRAPHIC',
      printWidth: '72mm',
      printResolution: '203dpi',
      initialCommand: '',
      cutterCommand: '1D,56,42,00',
      drawerCommand: '1B,70,00,19,FA'
    };
  }

  private getDefaultPrinterSettingsWithAdvanced() {
    return {
      ...this.getDefaultPrinterSettings(),
      advancedSettings: this.getDefaultAdvancedSettings()
    };
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onSave(): void {
    if (this.deviceForm.valid) {
      const formValue = this.deviceForm.getRawValue();
      if (!formValue.printerSettings.advancedSettingsEnabled) {
        formValue.printerSettings.advancedSettings = null;
      }
      this.saveDevice.emit(formValue);
    }
  }
  
  onTestPrint(): void {
    const model = this.deviceForm.get('printerSettings.model')?.value;
    if (model && model !== 'No Printer') {
      this.notificationService.showInfo(`Simulating test print command to: ${model}`);
    } else {
      this.notificationService.showError('Please select a printer model first.');
    }
  }

  onBackdropClick(event: MouseEvent): void {
      if (event.target === event.currentTarget) {
          this.onClose();
      }
  }
}
